import moment from 'moment';
import * as React from 'react';
import { InfoMessage } from '@common/components/info-message';

// Acceptance-criteria formats (Table Fields)
export const TABLE_VALID_DATE_FORMATS = [
  { value: 'M/D/YYYY', label: 'M/D/YYYY (e.g., 9/29/2025)' },
  { value: 'M/D/YY', label: 'M/D/YY (e.g., 9/29/25)' },
  { value: 'MMM D, YYYY', label: 'MMM D, YYYY (e.g., Sep 29, 2025)' },
  { value: 'MMMM D, YYYY', label: 'MMMM D, YYYY (e.g., September 29, 2025)' },
] as const;


/**
 * Single source of truth for all accepted formats.
 */
export const DATE_INPUT_FORMATS = [
  "M/D/YYYY",
  "MM/DD/YYYY",
  "M/DD/YYYY",
  "MM/D/YYYY",
  "M/D/YY",
  "MM/DD/YY",
  "M/DD/YY",
  "MM/D/YY",
  "MMM D, YYYY",
  "MMMM D, YYYY",
  "MMM D, YY",
  "MMMM D, YY",
] as const;

/**
 * Display-only patterns (if UI needs simplified display list).
 */
export const DATE_DISPLAY_FORMATS = ["MM/DD/YYYY"] as const;

/**
 * Check if string contains 4-digit year.
 */
function hasFourDigitYear(dateStr: string, format: string): boolean {
  const separators = ["/", ",", "-"];
  let parts: string[] = [];

  for (const sep of separators) {
    if (format.includes(sep)) {
      parts = dateStr.split(sep);
      break;
    }
  }

  if (parts.length > 0) {
    const year = parts[parts.length - 1]?.trim();
    return !!year && year.length === 4;
  }

  return false;
}

/**
 * Strict parsing logic (moved unchanged from controller).
 */
export function parseDateString(dateStr: string): Date | null {
  if (!dateStr || dateStr.trim() === "") {
    return null;
  }

  const trimmed = dateStr.trim();

  const textMonthPattern =
    /^(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|January|February|March|April|May|June|July|August|September|October|November|December)\s+\d{1,2},\s+\d{2,4}$/i;

  const isTextMonthFormat = textMonthPattern.test(trimmed);

  const monthNamePattern =
    /(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec|January|February|March|April|May|June|July|August|September|October|November|December)/gi;

  const monthMatches = trimmed.match(monthNamePattern);

  if (monthMatches && monthMatches.length > 1) {
    return null;
  }

  if (monthMatches && monthMatches.length === 1 && !isTextMonthFormat) {
    return null;
  }

  for (const format of DATE_INPUT_FORMATS) {
    try {
      if (isTextMonthFormat && format.includes("/")) continue;
      if (!isTextMonthFormat && format.includes("MMM")) continue;

      let parsed = moment.utc(trimmed, format, true);
      if (!parsed.isValid()) continue;

      const year = parsed.year();
      const month = parsed.month();
      const day = parsed.date();

      const recheck = moment.utc([year, month, day]);

      if (
        !recheck.isValid() ||
        recheck.year() !== year ||
        recheck.month() !== month ||
        recheck.date() !== day
      ) {
        continue;
      }

      const allowYear =
        hasFourDigitYear(trimmed, format) ||
        format.includes("YYYY") ||
        format.includes("YY");

      if (!allowYear) continue;

      return new Date(year, month, day);
    } catch {
      continue;
    }
  }

  return null;
}

/**
 * Public validation method.
 */
export function isValidDate(
  value: string | null | undefined,
): boolean {
  if (value == null || value === "") {
    return true;
  }

  return parseDateString(value) !== null;
}

/**
 * Convert a Date object to YYYY-MM-DD API format string.
 */
export function formatDateAsApi(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Parse a YYYY-MM-DD API format string to a local Date.
 */
export function parseApiDateString(dateStr: string): Date | null {
  const match = dateStr.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return new Date(parseInt(match[1]!, 10), parseInt(match[2]!, 10) - 1, parseInt(match[3]!, 10));
}
/** Display format for spreadsheet date columns. */
export const DATE_DISPLAY_FORMAT = "MM/DD/YYYY";
/**
 * Format string for react-datepicker (uses date-fns internally).
 * date-fns requires lowercase 'dd' and 'yyyy' (not 'DD' and 'YYYY').
 */
export const DATE_PICKER_FORMAT = "MM/dd/yyyy";

export type SpreadsheetDateValidationResult = {
  isValid: boolean;
  /** Normalized API value in YYYY/MM/DD format, or empty string when clearing */
  normalizedValue?: string;
};

/**
 * Validate spreadsheet date input using the same logic as datasheet date validation.
 * Returns normalized value in YYYY/MM/DD for API calls.
 */
export function validateSpreadsheetDateInput(
  raw: string
): SpreadsheetDateValidationResult {
  const value = raw?.trim?.() ?? "";
  if (value === "") {
    return { isValid: true, normalizedValue: "" };
  }
  const date = parseDateString(value);
  if (!date) {
    return { isValid: false };
  }
  return {
    isValid: true,
    normalizedValue: formatDateAsApi(date).replace(/-/g, "/"),
  };
}
/** Error message shown when spreadsheet date format is invalid. */
export function getSpreadsheetInvalidDateMessage(): string {
  return "Invalid date format. Dates must be entered as MM/DD/YYYY, for example: 01/21/2026.";
}

/** Formatted error content with bullet list of accepted date formats (matches AC). */
export function getSpreadsheetInvalidDateMessageContent(): React.ReactNode {
  return React.createElement(
    InfoMessage,
    null,
    'Invalid date format. Dates must be entered as:',
    React.createElement(
      'ul',
      { style: { marginTop: 4, paddingLeft: 20 } },
      TABLE_VALID_DATE_FORMATS.map(format =>
        React.createElement('li', { key: format.value }, format.label)
      )
    )
  );
}
